import { useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { Link } from "react-router-dom";
import { PATIENTS_BASE } from "../AdminPatients/patientConstants";
import { formatTimeRu, toDateInputValue } from "../../../utils/appointmentLabels";
import {
  filterAssignedBranches,
  pickDefaultBranchId,
  doctorBranchOptionsFromResponse,
} from "./doctorConstants";
import s from "../clinic.module.css";
import u from "../../ui/ui.module.css";
import { Button, Field, Input, Select, useToast } from "../../ui";

const STATUS = {
  pending: { label: "Ожидает", badge: u.badgeBlue },
  contacted: { label: "Связались", badge: u.badgePurple },
  booked: { label: "Записан", badge: u.badgeGreen },
  cancelled: { label: "Отменён", badge: u.badgeRed },
};

export default function AdminDoctorWaitlist({ doctorId, doctorBranchOptions }) {
  const toast = useToast();
  const [entries, setEntries] = useState([]);
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [offerId, setOfferId] = useState(null);
  const [offer, setOffer] = useState({
    date: toDateInputValue(),
    branch_id: "",
    service_id: "",
    slotIso: "",
  });
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({
    patient_id: "",
    preferred_date: "",
    notes: "",
  });

  const load = () => {
    clinicAPI.getDoctorWaitlist(doctorId).then((r) => setEntries(r.waitlist || []));
  };

  useEffect(() => {
    load();
    clinicAPI.getPatients({ limit: 200 }).then((r) => setPatients(r.patients || []));
    clinicAPI.getDoctorServices(doctorId).then((r) => {
      const list = (r.services || []).filter((x) => x.assigned);
      setServices(list.length ? list : r.services || []);
    });

    const applyBranches = (options) => {
      const list = filterAssignedBranches(options);
      setBranches(list);
      setOffer((o) => ({
        ...o,
        branch_id: list.some((b) => b.id === o.branch_id)
          ? o.branch_id
          : pickDefaultBranchId(list),
      }));
    };

    if (doctorBranchOptions?.length) {
      applyBranches(doctorBranchOptions);
    } else {
      clinicAPI
        .getDoctor(doctorId)
        .then((r) => applyBranches(doctorBranchOptionsFromResponse(r.doctor)))
        .catch(() => toast.error("Не удалось загрузить залы тренера"));
    }
  }, [doctorId, doctorBranchOptions, toast]);

  useEffect(() => {
    if (!offerId || !offer.branch_id || !offer.date || !offer.service_id) {
      setSlots([]);
      return;
    }
    clinicAPI
      .getSlots(doctorId, offer.branch_id, offer.date, offer.service_id)
      .then((r) => setSlots(r.slots || []))
      .catch(() => setSlots([]));
  }, [offerId, doctorId, offer.branch_id, offer.date, offer.service_id]);

  const add = async () => {
    if (!form.patient_id) {
      toast.error("Выберите клиента");
      return;
    }
    try {
      await clinicAPI.addDoctorWaitlist(doctorId, form);
      setForm({ patient_id: "", preferred_date: "", notes: "" });
      setShowForm(false);
      load();
      toast.success("Добавлено в очередь");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const openOffer = (entry) => {
    setOfferId(entry.id);
    setOffer({
      date: entry.preferred_date || toDateInputValue(),
      branch_id: entry.branch_id || offer.branch_id,
      service_id: entry.service_id || "",
      slotIso: "",
    });
  };

  const submitOffer = async () => {
    if (!offer.slotIso) {
      toast.error("Выберите слот");
      return;
    }
    try {
      await clinicAPI.offerWaitlistSlot(doctorId, offerId, {
        start_datetime: offer.slotIso,
        branch_id: offer.branch_id,
        service_id: offer.service_id,
      });
      toast.success("Клиент записан из очереди");
      setOfferId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  return (
    <div className={s.docTabWrap}>
      <div className={s.waitlistHeader}>
        <div>
          <h4 className={s.docSectionTitle} style={{ margin: 0 }}>
            Лист ожидания
          </h4>
          <p className={s.docSectionHint}>
            Клиенты без свободного слота — «Предложить слот» создаёт запись
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setShowForm((v) => !v)}>
          {showForm ? "Свернуть" : "+ Добавить"}
        </Button>
      </div>

      {showForm && (
        <div className={`${s.docSection} ${s.waitlistAddCard}`}>
          <div className={s.waitlistAddGrid}>
            <Field label="Клиент">
              <Select
                value={form.patient_id}
                onChange={(e) =>
                  setForm({ ...form, patient_id: e.target.value })
                }
              >
                <option value="">Выберите клиента</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name || p.email}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Желаемая дата">
              <Input
                type="date"
                value={form.preferred_date}
                onChange={(e) =>
                  setForm({ ...form, preferred_date: e.target.value })
                }
              />
            </Field>
            <Field label="Комментарий">
              <Input
                placeholder="Удобное время…"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Field>
          </div>
          <Button type="button" onClick={add}>
            Добавить в очередь
          </Button>
        </div>
      )}

      {offerId && (
        <div className={`${s.docSection} ${s.waitlistOfferPanel}`}>
          <h5 className={s.docSectionTitle}>Предложить слот</h5>
          <div className={s.waitlistAddGrid}>
            <Field label="Дата">
              <Input
                type="date"
                value={offer.date}
                onChange={(e) =>
                  setOffer({ ...offer, date: e.target.value, slotIso: "" })
                }
              />
            </Field>
            <Field label="Зал">
              <Select
                value={offer.branch_id}
                onChange={(e) =>
                  setOffer({ ...offer, branch_id: e.target.value, slotIso: "" })
                }
              >
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Услуга">
              <Select
                value={offer.service_id}
                onChange={(e) =>
                  setOffer({ ...offer, service_id: e.target.value, slotIso: "" })
                }
              >
                <option value="">Выберите</option>
                {services.map((sv) => (
                  <option key={sv.id} value={sv.id}>
                    {sv.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {slots.length > 0 ? (
            <div className={s.slotPickGrid}>
              {slots.map((sl) => (
                <button
                  key={sl.start_datetime}
                  type="button"
                  className={
                    offer.slotIso === sl.start_datetime
                      ? s.slotPickActive
                      : s.slotPickBtn
                  }
                  onClick={() =>
                    setOffer({ ...offer, slotIso: sl.start_datetime })
                  }
                >
                  {formatTimeRu(sl.start_datetime)}
                </button>
              ))}
            </div>
          ) : (
            <p className={s.docEmptyHint}>Нет слотов — проверьте расписание</p>
          )}
          <div className={s.scheduleActions}>
            <Button type="button" onClick={submitOffer}>
              Записать
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setOfferId(null)}
            >
              Отмена
            </Button>
          </div>
        </div>
      )}

      {entries.length === 0 ? (
        <p className={s.docEmptyHint}>Очередь пуста</p>
      ) : (
        <ul className={s.waitlistCards}>
          {entries.map((e) => {
            const st = STATUS[e.status] || STATUS.pending;
            return (
              <li key={e.id} className={s.waitlistCard}>
                <div className={s.waitlistCardMain}>
                  {e.patient?.id ? (
                    <Link
                      to={`${PATIENTS_BASE}/${e.patient.id}`}
                      className={s.waitlistPatient}
                    >
                      {e.patient.full_name || e.patient.email}
                    </Link>
                  ) : (
                    <span>—</span>
                  )}
                  <span className={s.waitlistDate}>
                    {e.preferred_date
                      ? new Date(`${e.preferred_date}T12:00:00`).toLocaleDateString(
                          "ru-RU",
                          { day: "numeric", month: "short" },
                        )
                      : "Любая дата"}
                  </span>
                  {e.service?.name && (
                    <span className={s.waitlistNote}>{e.service.name}</span>
                  )}
                  {e.notes && (
                    <span className={s.waitlistNote}>{e.notes}</span>
                  )}
                </div>
                <div className={s.waitlistCardActions}>
                  <span className={`${u.badge} ${st.badge}`}>{st.label}</span>
                  {e.status === "pending" || e.status === "contacted" ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => openOffer(e)}
                    >
                      Предложить слот
                    </Button>
                  ) : null}
                  <Select
                    className={s.waitlistStatusSelect}
                    value={e.status}
                    onChange={async (ev) => {
                      await clinicAPI.updateDoctorWaitlist(doctorId, e.id, {
                        status: ev.target.value,
                      });
                      load();
                    }}
                  >
                    {Object.entries(STATUS).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v.label}
                      </option>
                    ))}
                  </Select>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
